import { FC, useState, useCallback, useEffect, useMemo, useRef, useContext } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Grid,
  Stack,
  IconButton,
  Button,
  Switch,
  FormControlLabel,
  useTheme,
  Select,
  MenuItem,
  OutlinedInput,
} from '@mui/material';
import { Add, Remove, OpenInNew, Edit, Check, Close, CloudUpload, Settings } from '@mui/icons-material';
import { uploadImage } from '../../../../../../apis/cdn.apis';
import { useDebounce } from '../../../../../../hooks/useDebounce';
import { TableConfigurationDialog, TableData, LegacyTableData } from '../../../widgets';
import { AppContext } from '../../../../../../context';
import { CellConfigDialog } from '../../../../../../components/Dialogs/CellConfig';
import { configWrapperStyles, configFieldStyles } from '../../../widgets/TableConfigurationDialog/styles';
import { ColorPicker } from '../../../../../../components/ColorPicker';
import moment from 'moment';
import { getNextPublishingDate as computeNextPublishingDate, isPublishingDateOverdue } from '../../../../../../utils/automationSchedule';

export interface SectionHeaders {
  stammdaten: string;
  monthlyCommentary: string;
  indexedChart: string;
  cumulativePerformance: string;
  rollingPerformance: string;
  investmentStrategy: string;
  awards: string;
  top10Stocks: string;
  sectorAllocation: string;
  currencyAllocation: string;
  assetAllocation: string;
  opportunities: string;
  risks: string;
  salesProspectus: string;
  legalNotes: string;
  chartImageTitle: string;
  explanatoryTextTitle: string;
}

export const SECTION_HEADER_DEFAULTS: SectionHeaders = {
  stammdaten: 'Stammdaten',
  monthlyCommentary: 'Monatskommentar des Fondsmanagers',
  indexedChart: 'Indexierte Wertentwicklung seit Auflage (EUR, brutto, in %)',
  cumulativePerformance: 'Kumulierte Wertentwicklung in EUR (brutto, in %)',
  rollingPerformance: 'Rollierende Wertentwicklung',
  investmentStrategy: 'Anlagestrategie',
  awards: 'Auszeichnungen',
  top10Stocks: 'Top 10 Aktienpositionen (in %)',
  sectorAllocation: 'Sektorallokation',
  currencyAllocation: 'Währungsallokation',
  assetAllocation: 'Vermögensaufteilung',
  opportunities: 'CHANCEN',
  risks: 'RISIKEN',
  salesProspectus: 'VERKAUFSPROSPEKT',
  legalNotes: 'RECHTLICHE HINWEISE',
  chartImageTitle: 'Chart-Platzhalter',
  explanatoryTextTitle: 'Erläuterungen zur Wertentwicklung',
};

interface PdfConfigProps {
  data: any;
  onChange: (data: any) => void;
  /** Optional: parent registers flush for pending debounced edits (template 698422… implements) */
  onRegisterFlush?: (flush: () => void) => void;
  isAutomated?: boolean;
  lastPublishedAt?: string | null;
  automationFrequency?: 'daily' | 'weekly' | 'monthly' | 'yearly';
}

export const PdfConfig: FC<PdfConfigProps> = ({ data, onChange, isAutomated = false, lastPublishedAt = null, automationFrequency = 'daily' }) => {
  const { document } = useContext(AppContext);
  const theme = useTheme();
  const [formData, setFormData] = useState(data || {});
  const [editingHeader, setEditingHeader] = useState<{ key: keyof SectionHeaders; value: string } | null>(null);
  const [cumulativePerformanceDialogOpen, setCumulativePerformanceDialogOpen] = useState(false);
  const [openCellConfigDialog, setOpenCellConfigDialog] = useState<{ section: string; field: string } | null>(null);
  // Local state for ISIN input to allow immediate UI updates
  const [localHistoryIsin, setLocalHistoryIsin] = useState(data?.historyIsin || '');
  // Debounce the ISIN value to prevent excessive API calls
  const debouncedHistoryIsin = useDebounce(localHistoryIsin, 500);
  // Ref to track the previous debounced value to avoid unnecessary updates
  const prevDebouncedIsinRef = useRef<string>(debouncedHistoryIsin);
  
  // Get ISIN from document template
  const isin = document?.template?.isins?.[0] || formData?.historyIsin || '';

  const sectionHeaders: SectionHeaders = useMemo(() => ({
    ...SECTION_HEADER_DEFAULTS,
    ...(formData.sectionHeaders || {}),
  }), [formData.sectionHeaders]);

  useEffect(() => {
    setFormData(data || {});
    setLocalHistoryIsin(data?.historyIsin || '');
    prevDebouncedIsinRef.current = data?.historyIsin || '';
  }, [data]);

  // Update formData when debounced ISIN value changes
  useEffect(() => {
    if (debouncedHistoryIsin !== prevDebouncedIsinRef.current) {
      prevDebouncedIsinRef.current = debouncedHistoryIsin;
      setFormData((prev) => {
        const updatedData = { ...prev, historyIsin: debouncedHistoryIsin };
        onChange(updatedData);
        return updatedData;
      });
    }
  }, [debouncedHistoryIsin, onChange]);

  function toDateInputValue(value?: string) {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const iso = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
    return iso.slice(0, 10);
  }

  // Calculate next publishing date based on automation frequency
  const getNextPublishingDate = (): string => {
    if (!isAutomated) return '';
    const baseDate = lastPublishedAt ? moment(lastPublishedAt) : moment();
    if (!baseDate.isValid()) return '';
    return computeNextPublishingDate(baseDate, automationFrequency).format('YYYY-MM-DD');
  };

  const nextPublishingDate = getNextPublishingDate();

  const handleFieldChange = useCallback((field: string, value: any) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  const handleObjectFieldChange = useCallback((objectKey: string, field: string, value: any) => {
    const updatedData = {
      ...formData,
      [objectKey]: {
        ...formData[objectKey],
        [field]: value
      }
    };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  // Cumulative Performance table handlers - convert to new V1-style format for dialog
  const getCumulativePerformanceData = useCallback(() => {
    const perf = formData.cumulativePerformance;
    // New V1-style structure (labels/values/columns)
    if (perf && perf.labels && Array.isArray(perf.labels) && perf.values && Array.isArray(perf.values)) {
      return perf;
    }
    // Old key-value structure - migrate on the fly
    if (perf && typeof perf === 'object' && !perf.labels) {
      return {
        labels: ['1 Monat', '6 Monate', '1 Jahr', '3 Jahre', '5 Jahre', 'Lfd. Jahr', 'seit Auflage'],
        values: [[
          perf.oneMonth || '+1,76',
          perf.sixMonths || '-3,04',
          perf.oneYear || '-2,54',
          perf.threeYears || '-1,07',
          perf.fiveYears || '-0,68',
          perf.currentYear || '+0,95',
          perf.sinceInception || '+95,38'
        ]],
        columns: new Array(7).fill({}).map(() => ({ type: 'text' as const })),
      };
    }
    // Default structure
    return {
      labels: ['1 Monat', '6 Monate', '1 Jahr', '3 Jahre', '5 Jahre', 'Lfd. Jahr', 'seit Auflage'],
      values: [['+1,76', '-3,04', '-2,54', '-1,07', '-0,68', '+0,95', '+95,38']],
      columns: new Array(7).fill({}).map(() => ({ type: 'text' as const })),
    };
  }, [formData.cumulativePerformance]);

  // Dialog handlers
  const handleOpenCumulativePerformanceDialog = useCallback(() => {
    setCumulativePerformanceDialogOpen(true);
  }, []);

  const handleCloseCumulativePerformanceDialog = useCallback(() => {
    setCumulativePerformanceDialogOpen(false);
  }, []);

  const handleSaveCumulativePerformanceDialog = useCallback((data: TableData | LegacyTableData) => {
    // Always save in new format (labels/values/columns)
    const newFormatData = 'labels' in data ? data : {
      labels: data.headers,
      values: data.rows.map(row => row.values),
      columns: new Array(data.headers.length).fill({}).map(() => ({ type: 'text' as const })),
    };
    handleFieldChange('cumulativePerformance', newFormatData);
  }, [handleFieldChange]);

  // Helper function to check if a value is dynamic
  const isValueDynamic = useCallback((section: string, field: string): boolean => {
    const value = formData[section]?.[field];
    return typeof value === 'object' && value?.isDynamic === true;
  }, [formData]);

  // Helper function to get the display value (text or config badges)
  const getValueDisplay = useCallback((section: string, field: string): string => {
    const value = formData[section]?.[field];
    if (typeof value === 'object' && value?.isDynamic) {
      return ''; // Dynamic values show config badges, not text
    }
    return typeof value === 'string' ? value : '';
  }, [formData]);

  // Helper function to toggle dynamic mode
  const handleToggleDynamic = useCallback((section: string, field: string) => {
    const currentValue = formData[section]?.[field];
    const isCurrentlyDynamic = typeof currentValue === 'object' && currentValue?.isDynamic === true;
    
    const updatedData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: isCurrentlyDynamic
          ? (typeof currentValue === 'string' ? currentValue : '') // Convert back to string
          : {
              isDynamic: true,
              fields: [],
            },
      },
    };
    setFormData(updatedData);
    onChange(updatedData);
  }, [formData, onChange]);

  // Helper function to handle cell config dialog
  const handleOpenCellConfig = useCallback((section: string, field: string) => {
    setOpenCellConfigDialog({ section, field });
  }, []);

  const handleCloseCellConfig = useCallback(() => {
    setOpenCellConfigDialog(null);
  }, []);

  const handleSetCellConfig = useCallback((config: any) => {
    if (!openCellConfigDialog) return;
    
    const { section, field } = openCellConfigDialog;
    const updatedData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: config,
      },
    };
    setFormData(updatedData);
    onChange(updatedData);
    setOpenCellConfigDialog(null);
  }, [formData, onChange, openCellConfigDialog]);

  // Helper function to render dynamic value field
  const renderDynamicValueField = useCallback((
    section: string,
    field: string,
    label: string,
    defaultValue: string
  ) => {
    const isDynamic = isValueDynamic(section, field);
    const value = getValueDisplay(section, field);
    const config = formData[section]?.[field];

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
        <Box sx={{ flex: 1 }}>
          {isDynamic ? (
            <Box sx={configWrapperStyles}>
              {config?.fields?.map((fieldConfig: any, index: number) => (
                <Box
                  key={index}
                  sx={configFieldStyles(theme, fieldConfig.type)}
                >
                  <Typography variant="subtitle2">
                    {fieldConfig.type === 'text'
                      ? fieldConfig.text
                      : `${fieldConfig.table || '(table)'}.${fieldConfig.field || '(field)'}`}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <TextField
              label={label}
              value={value || defaultValue}
              onChange={(e) => handleObjectFieldChange(section, field, e.target.value)}
              fullWidth
              size="small"
            />
          )}
        </Box>
        {isDynamic && (
          <IconButton
            size="small"
            onClick={() => handleOpenCellConfig(section, field)}
            sx={{ width: 32, height: 32 }}
          >
            <Settings fontSize="small" />
          </IconButton>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={isDynamic}
              onChange={() => handleToggleDynamic(section, field)}
              color="secondary"
              size="small"
            />
          }
          label="Dynamic"
          sx={{ m: 0, minWidth: 80 }}
        />
      </Box>
    );
  }, [formData, isValueDynamic, getValueDisplay, handleToggleDynamic, handleOpenCellConfig, handleObjectFieldChange, theme]);

  const handleScrollTo = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSectionHeaderChange = useCallback((key: keyof SectionHeaders, value: string) => {
    const updatedHeaders: SectionHeaders = {
      ...sectionHeaders,
      [key]: value,
    };
    const updatedData = {
      ...formData,
      sectionHeaders: updatedHeaders,
    };
    setFormData(updatedData);
    onChange(updatedData);
    setEditingHeader(null);
  }, [formData, onChange, sectionHeaders]);

  const handleConfirmSectionHeaderEdit = useCallback(() => {
    if (!editingHeader) return;
    handleSectionHeaderChange(editingHeader.key, editingHeader.value);
  }, [editingHeader, handleSectionHeaderChange]);

  const renderSectionHeaderControls = (
    key: keyof SectionHeaders,
    sectionId?: string,
    options: { variant?: 'subtitle1' | 'subtitle2'; fontWeight?: number | string } = {},
  ) => {
    const { variant = 'subtitle1', fontWeight = 'bold' } = options;
    const isEditing = editingHeader?.key === key;
    const displayTitle = isEditing ? (editingHeader?.value || '') : sectionHeaders[key];

    return (
      <Box display="flex" alignItems="center" gap={2}>
        {isEditing ? (
          <>
            <TextField
              value={editingHeader?.value || ''}
              onChange={(e) => setEditingHeader((prev) => (prev ? { ...prev, value: e.target.value } : prev))}
              size="small"
              autoFocus
              InputProps={{ sx: { fontWeight } }}
            />
            <Check
              fontSize="small"
              color="secondary"
              onClick={handleConfirmSectionHeaderEdit}
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
            <Close
              fontSize="small"
              color="secondary"
              onClick={() => setEditingHeader(null)}
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
          </>
        ) : (
          <>
            <Typography variant={variant} fontWeight={fontWeight}>
              {displayTitle}
            </Typography>
            <Edit
              fontSize="small"
              color="secondary"
              onClick={() => setEditingHeader({ key, value: sectionHeaders[key] })}
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
          </>
        )}
        {sectionId ? (
          <OpenInNew
            onClick={() => handleScrollTo(sectionId)}
            color="secondary"
            sx={{
              width: 20,
              height: 20,
              cursor: 'pointer',
              transition: 'color 120ms ease',
              '&:hover': { color: 'secondary.dark' },
            }}
          />
        ) : null}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2, my: 64, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography variant="h6" gutterBottom>
        PDF Configuration
      </Typography>

      {/* Header Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">Header</Typography>
          </Box>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={formData.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Subtitle"
              value={formData.subtitle || ''}
              onChange={(e) => handleFieldChange('subtitle', e.target.value)}
              fullWidth
              multiline
              rows={2}
              size="small"
            />
            <TextField
              label={isAutomated ? "Last Published At" : "Published At"}
              type="date"
              value={isAutomated && lastPublishedAt ? toDateInputValue(lastPublishedAt) : toDateInputValue(formData.published_at)}
              onChange={(e) => handleFieldChange('published_at', e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              disabled={isAutomated}
              helperText={isAutomated ? "Automated documents are published automatically by the system" : undefined}
            />
            {isAutomated && nextPublishingDate && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Next publication at {moment(nextPublishingDate).format('DD.MM.YYYY')}
                {isPublishingDateOverdue(nextPublishingDate) ? ' (overdue)' : ''}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>
      
      {/* Chart Image Upload Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('chartImageTitle', 'chart-image-section')}
          </Box>
          <Stack spacing={2}>
            {formData.chartImageUrl && (
              <Box sx={{ mb: 2 }}>
                <img 
                  src={formData.chartImageUrl} 
                  alt="Chart" 
                  style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} 
                />
              </Box>
            )}
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="chart-image-upload"
              type="file"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  try {
                    const res = await uploadImage(file);
                    if (res?.status && res?.fileName) {
                      // Check if fileName is already a full URL
                      let imageUrl = res.fileName;
                      if (!res.fileName.startsWith('http://') && !res.fileName.startsWith('https://')) {
                        // If it's just the key, construct the full URL
                        imageUrl = `https://cixon-finance-docs.s3.amazonaws.com/${res.fileName}`;
                      }
                      handleFieldChange('chartImageUrl', imageUrl);
                    }
                  } catch (error) {
                    console.error('Error uploading image:', error);
                  }
                }
              }}
            />
            <label htmlFor="chart-image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                fullWidth
              >
                {formData.chartImageUrl ? 'Replace Chart Image' : 'Upload Chart Image'}
              </Button>
            </label>
            {formData.chartImageUrl && (
              <Button
                variant="text"
                color="error"
                onClick={() => handleFieldChange('chartImageUrl', '')}
                fullWidth
              >
                Remove Image
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Master Data (Stammdaten) Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('stammdaten', 'stammdaten-section')}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'fondskategorie', 'Fondskategorie', 'Aktienfonds')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'auflagedatum', 'Auflagedatum', '02.09.2024')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'wkn', 'WKN', 'A3EWBE')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'isin', 'ISIN', 'DE000A3EWBE8')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'erstausgabepreis', 'Erstausgabepreis', '148,37 EUR')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'fondsvolumen', 'Fondsvolumen', '9,96 Mio. EUR')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'kursNav', 'Kurs (NAV)', '146,39 EUR')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'fondswaehrung', 'Fondswährung', 'EUR')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'fondsdomizil', 'Fondsdomizil', 'DE')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'geschaeftsjahresende', 'Geschäftsjahresende', '31-Aug.')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'ertragsverwendung', 'Ertragsverwendung', 'Thesaurierend')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'rechtsform', 'Rechtsform', 'OGAW-Sondervermögen')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'risikoErtragsprofil', 'Risiko-/Ertragsprofil (SRI 1-7)*', '4')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'sparplanfaehig', 'Sparplanfähig', 'ja')}
            </Grid>
            <Grid item xs={12}>
              {renderDynamicValueField('masterData', 'mindestzeichnungbetrag', 'Mindestzeichnungbetrag', '100.000 EUR')}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Fees (Gebühren) Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">Gebühren</Typography>
            <OpenInNew
              onClick={() => handleScrollTo('fees-section')}
              color="secondary"
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
          </Box>
          <Stack spacing={2}>
            {renderDynamicValueField('fees', 'ausgabeaufschlag', 'Ausgabeaufschlag', '0% (MAX 5%)')}
            {renderDynamicValueField('fees', 'laufendeKostenTer', 'Laufende Kosten (TER)', '1,94%')}
            {renderDynamicValueField('fees', 'davonVerwaltungsgebuehr', 'davon Verwaltungsgebühr', '1,23% (MAX 2%)')}
          </Stack>
        </CardContent>
      </Card>

      {/* Fund Manager Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">Fund Manager</Typography>
            <OpenInNew
              onClick={() => handleScrollTo('fund-manager-section')}
              color="secondary"
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
          </Box>
          <Stack spacing={2}>
            <TextField
              label="Name"
              value={formData.fundManager?.name || ''}
              onChange={(e) => handleObjectFieldChange('fundManager', 'name', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Company"
              value={formData.fundManager?.company || ''}
              onChange={(e) => handleObjectFieldChange('fundManager', 'company', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Research & Strategie"
              value={formData.fundManager?.research || ''}
              onChange={(e) => handleObjectFieldChange('fundManager', 'research', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Company 2"
              value={formData.fundManager?.company2 || ''}
              onChange={(e) => handleObjectFieldChange('fundManager', 'company2', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Phone"
              value={formData.fundManager?.phone || ''}
              onChange={(e) => handleObjectFieldChange('fundManager', 'phone', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Hedging Ratio"
              value={formData.fundManager?.hedgingRatio || ''}
              onChange={(e) => handleObjectFieldChange('fundManager', 'hedgingRatio', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Photo URL"
              value={formData.fundManager?.photoUrl || ''}
              onChange={(e) => handleObjectFieldChange('fundManager', 'photoUrl', e.target.value)}
              fullWidth
              size="small"
              placeholder="URL to fund manager photo"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Monthly Commentary Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('monthlyCommentary', 'monthly-commentary-section')}
          </Box>
          <TextField
            label="Monthly Commentary"
            value={formData.monthlyCommentary || ''}
            onChange={(e) => handleFieldChange('monthlyCommentary', e.target.value)}
            fullWidth
            multiline
            rows={15}
            size="small"
            placeholder="Enter monthly commentary..."
          />
        </CardContent>
      </Card>

      {/* Performance notice (MiFID: no performance display under 12 months) */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Hinweis Wertentwicklung</Typography>
          <TextField
            label="Hinweistext (ersetzt Chart und Tabellenwerte, solange gesetzt)"
            value={formData.performanceNotice || ''}
            onChange={(e) => handleFieldChange('performanceNotice', e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
            helperText="Leer lassen, sobald die Wertentwicklung gezeigt werden darf. Gilt für Chart, kumulierte und rollierende Wertentwicklung."
          />
        </CardContent>
      </Card>

      {/* Chart Settings Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">Chart Settings</Typography>
            <OpenInNew
              onClick={() => handleScrollTo('indexed-chart-section')}
              color="secondary"
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="ISIN (override)"
                placeholder="e.g. DE000A3EWBE8"
                value={localHistoryIsin}
                onChange={(e) => setLocalHistoryIsin(e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="End date (until)"
                type="date"
                value={toDateInputValue(formData.historyUntil)}
                onChange={(e) => handleFieldChange('historyUntil', e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Legend label"
                value={formData.historyLegendLabel || formData.title || ''}
                onChange={(e) => handleFieldChange('historyLegendLabel', e.target.value)}
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Explanatory Text Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('explanatoryTextTitle', 'explanatory-text-section')}
          </Box>
          <TextField
            label="Explanatory Text"
            value={formData.explanatoryText || ''}
            onChange={(e) => handleFieldChange('explanatoryText', e.target.value)}
            fullWidth
            multiline
            rows={8}
            size="small"
            placeholder="Enter explanatory text about performance..."
          />
        </CardContent>
      </Card>

      {/* Awards Image Upload Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">Awards Image</Typography>
            <OpenInNew
              onClick={() => handleScrollTo('awards-section')}
              color="secondary"
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
          </Box>
          <Stack spacing={2}>
            {formData.awardsImageUrl && (
              <Box sx={{ mb: 2 }}>
                <img 
                  src={formData.awardsImageUrl} 
                  alt="Awards" 
                  style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} 
                />
              </Box>
            )}
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="awards-image-upload"
              type="file"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) {
                  try {
                    const res = await uploadImage(file);
                    if (res?.status && res?.fileName) {
                      // Check if fileName is already a full URL
                      let imageUrl = res.fileName;
                      if (!res.fileName.startsWith('http://') && !res.fileName.startsWith('https://')) {
                        // If it's just the key, construct the full URL
                        imageUrl = `https://cixon-finance-docs.s3.amazonaws.com/${res.fileName}`;
                      }
                      handleFieldChange('awardsImageUrl', imageUrl);
                    }
                  } catch (error) {
                    console.error('Error uploading image:', error);
                  }
                }
              }}
            />
            <label htmlFor="awards-image-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                fullWidth
              >
                {formData.awardsImageUrl ? 'Replace Awards Image' : 'Upload Awards Image'}
              </Button>
            </label>
            {formData.awardsImageUrl && (
              <Button
                variant="text"
                color="error"
                onClick={() => handleFieldChange('awardsImageUrl', '')}
                fullWidth
              >
                Remove Image
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Rolling Performance Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {renderSectionHeaderControls('rollingPerformance', 'rolling-performance-section')}
          </Box>
          
          <Box sx={{ my: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Select
                value={formData.rollingPerformance?.chartType || "Manual"}
                onChange={(e) => {
                  const newChartType = e.target.value;
                  // Update both chartType and automationType in a single operation
                  const updatedData = {
                    ...formData,
                    rollingPerformance: {
                      ...formData.rollingPerformance,
                      chartType: newChartType,
                      ...(newChartType === "Automated" && { automationType: 'all' })
                    }
                  };
                  setFormData(updatedData);
                  onChange(updatedData);
                }}
                sx={{ minWidth: 150 }}
                size="small"
              >
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Automated">Automated</MenuItem>
              </Select>
            </Box>

            {formData.rollingPerformance?.chartType === "Automated" && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <OutlinedInput
                    value={formData.rollingPerformance?.isin || ""}
                    onChange={(e) => {
                      handleObjectFieldChange('rollingPerformance', 'isin', e.target.value);
                    }}
                    placeholder="ISIN"
                    size="small"
                    sx={{ width: 200 }}
                  />
                  <Select
                    value={formData.rollingPerformance?.automationType || "all"}
                    onChange={(e) => {
                      handleObjectFieldChange('rollingPerformance', 'automationType', e.target.value);
                    }}
                    size="small"
                    sx={{ width: 200 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="gross_perf_year_as_of_today">Gross</MenuItem>
                    <MenuItem value="perf_year_as_of_today">Net</MenuItem>
                  </Select>
                </Box>
                {(!formData.rollingPerformance?.automationType || formData.rollingPerformance?.automationType === 'gross_perf_year_as_of_today') && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">Gross:</Typography>
                    <OutlinedInput
                      value={formData.rollingPerformance?.legend?.gross_perf_year_as_of_today || ''}
                      onChange={(e) => {
                        handleObjectFieldChange('rollingPerformance', 'legend', {
                          ...(formData.rollingPerformance?.legend || {}),
                          gross_perf_year_as_of_today: e.target.value
                        });
                      }}
                      placeholder="e.g., brutto"
                      size="small"
                      sx={{ flex: 1, maxWidth: 400 }}
                    />
                  </Box>
                )}
                {(formData.rollingPerformance?.automationType === 'perf_year_as_of_today') && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption">Net:</Typography>
                    <OutlinedInput
                      value={formData.rollingPerformance?.legend?.perf_year_as_of_today || ''}
                      onChange={(e) => {
                        handleObjectFieldChange('rollingPerformance', 'legend', {
                          ...(formData.rollingPerformance?.legend || {}),
                          perf_year_as_of_today: e.target.value
                        });
                      }}
                      placeholder="e.g., netto (mit 5% Ausgabea"
                      size="small"
                      sx={{ flex: 1, maxWidth: 400 }}
                    />
                  </Box>
                )}
                {(formData.rollingPerformance?.automationType === 'all') && (
                  <Box sx={{ display: 'flex', gap: 8 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="caption">Gross:</Typography>
                      <OutlinedInput
                        fullWidth
                        value={formData.rollingPerformance?.legend?.gross_perf_year_as_of_today || ''}
                        onChange={(e) => {
                          handleObjectFieldChange('rollingPerformance', 'legend', {
                            ...(formData.rollingPerformance?.legend || {}),
                            gross_perf_year_as_of_today: e.target.value
                          });
                        }}
                        placeholder="e.g., brutto"
                        size="small"
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Typography variant="caption">Net:</Typography>
                      <OutlinedInput
                        fullWidth
                        value={formData.rollingPerformance?.legend?.perf_year_as_of_today || ''}
                        onChange={(e) => {
                          handleObjectFieldChange('rollingPerformance', 'legend', {
                            ...(formData.rollingPerformance?.legend || {}),
                            perf_year_as_of_today: e.target.value
                          });
                        }}
                        placeholder="e.g., netto (mit 5% Ausgabea"
                        size="small"
                      />
                    </Box>
                  </Box>
                )}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Bar Color Sets</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {(formData.rollingPerformance?.barColorSets || []).map((color: string, index: number) => (
                      <ColorPicker
                        key={index}
                        color={color || '#1976d2'}
                        onChange={(newHex: string) => {
                          const next = (formData.rollingPerformance?.barColorSets || []).map((c: string, i: number) => i === index ? newHex : c);
                          handleObjectFieldChange('rollingPerformance', 'barColorSets', next);
                        }}
                        height={150}
                      >
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            border: '1px solid #ddd',
                            backgroundColor: color || '#1976d2',
                            cursor: 'pointer',
                          }}
                        />
                      </ColorPicker>
                    ))}
                    <IconButton
                      size="small"
                      onClick={() => {
                        const next = [...(formData.rollingPerformance?.barColorSets || []), '#1976d2'];
                        handleObjectFieldChange('rollingPerformance', 'barColorSets', next);
                      }}
                      sx={{ width: 32, height: 32, border: '1px dashed #bbb' }}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          {formData.rollingPerformance?.chartType !== "Automated" && (
            <>
              <Box display="flex" alignItems="center" justifyContent="flex-end" mb={2}>
                <IconButton size="small" onClick={() => {
                  const periods = [...(formData.rollingPerformance?.periods || []), { label: '', netto: 0, brutto: 0 }];
                  handleObjectFieldChange('rollingPerformance', 'periods', periods);
                }} aria-label="Add period">
                  <Add fontSize="small" />
                </IconButton>
              </Box>
              <Stack spacing={8}>
                {(formData.rollingPerformance?.periods || []).map((period: any, index: number) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                      label={`Label ${index + 1}`}
                      value={period.label || ''}
                      onChange={(e) => {
                        const periods = [...(formData.rollingPerformance?.periods || [])];
                        periods[index] = { ...period, label: e.target.value };
                        handleObjectFieldChange('rollingPerformance', 'periods', periods);
                      }}
                      fullWidth
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Netto"
                      type="number"
                      value={period.netto || ''}
                      onChange={(e) => {
                        const periods = [...(formData.rollingPerformance?.periods || [])];
                        periods[index] = { ...period, netto: parseFloat(e.target.value) || 0 };
                        handleObjectFieldChange('rollingPerformance', 'periods', periods);
                      }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                    <TextField
                      label="Brutto"
                      type="number"
                      value={period.brutto || ''}
                      onChange={(e) => {
                        const periods = [...(formData.rollingPerformance?.periods || [])];
                        periods[index] = { ...period, brutto: parseFloat(e.target.value) || 0 };
                        handleObjectFieldChange('rollingPerformance', 'periods', periods);
                      }}
                      size="small"
                      sx={{ width: 120 }}
                    />
                    <IconButton
                      onClick={() => {
                        const periods = [...(formData.rollingPerformance?.periods || [])];
                        periods.splice(index, 1);
                        handleObjectFieldChange('rollingPerformance', 'periods', periods);
                      }}
                      color="error"
                      size="small"
                      aria-label={`Remove period ${index + 1}`}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      {/* Cumulative Performance Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('cumulativePerformance', 'cumulative-performance-section')}
          </Box>
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} width="100%">
              <Button
                variant="outlined"
                startIcon={<Settings />}
                onClick={handleOpenCumulativePerformanceDialog}
                size="small"
              >
                Configure Table
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Cumulative Performance Table Configuration Dialog */}
      <TableConfigurationDialog
        open={cumulativePerformanceDialogOpen}
        onClose={handleCloseCumulativePerformanceDialog}
        onSave={handleSaveCumulativePerformanceDialog}
        initialData={getCumulativePerformanceData()}
        title="Configure Cumulative Performance Table"
        isin={isin}
      />

      {/* Cell Configuration Dialog */}
      {openCellConfigDialog && (
        <CellConfigDialog
          isin={isin}
          config={formData[openCellConfigDialog.section]?.[openCellConfigDialog.field]}
          open={!!openCellConfigDialog}
          onClose={handleCloseCellConfig}
          onConfirm={handleSetCellConfig}
        />
      )}

      {/* Investment Strategy Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('investmentStrategy', 'investment-strategy-section')}
          </Box>
          <TextField
            label="Investment Strategy"
            value={formData.investmentStrategy || ''}
            onChange={(e) => handleFieldChange('investmentStrategy', e.target.value)}
            fullWidth
            multiline
            rows={6}
            size="small"
            placeholder="Enter investment strategy..."
          />
        </CardContent>
      </Card>

      {/* Target Group, Investment Horizon, Risk Indicator Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">Target Group & Risk</Typography>
            <OpenInNew
              onClick={() => handleScrollTo('target-group-risk-section')}
              color="secondary"
              sx={{
                width: 20,
                height: 20,
                cursor: 'pointer',
                transition: 'color 120ms ease',
                '&:hover': { color: 'secondary.dark' },
              }}
            />
          </Box>
          <Stack spacing={2}>
            <TextField
              label="Target Group (Zielgruppe)"
              value={formData.targetGroup || ''}
              onChange={(e) => handleFieldChange('targetGroup', e.target.value)}
              fullWidth
              multiline
              rows={4}
              size="small"
            />
            <TextField
              label="Investment Horizon (Anlagehorizont)"
              value={formData.investmentHorizon || ''}
              onChange={(e) => handleFieldChange('investmentHorizon', e.target.value)}
              fullWidth
              multiline
              rows={3}
              size="small"
            />
            <TextField
              label="Risk Indicator (1-7)"
              type="number"
              inputProps={{ min: 1, max: 7 }}
              value={formData.riskIndicator || ''}
              onChange={(e) => handleFieldChange('riskIndicator', parseInt(e.target.value) || 0)}
              fullWidth
              size="small"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Opportunities Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('opportunities', 'opportunities-section')}
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter one opportunity per line
          </Typography>
          <TextField
            label="Opportunities"
            value={Array.isArray(formData.opportunities) ? formData.opportunities.join('\n') : ''}
            onChange={(e) => {
              const lines = e.target.value.split('\n').filter(line => line.trim());
              handleFieldChange('opportunities', lines);
            }}
            fullWidth
            multiline
            rows={6}
            size="small"
            placeholder="Enter opportunities, one per line..."
          />
        </CardContent>
      </Card>

      {/* Risks Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('risks', 'risks-section')}
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter one risk per line
          </Typography>
          <TextField
            label="Risks"
            value={Array.isArray(formData.risks) ? formData.risks.join('\n') : ''}
            onChange={(e) => {
              const lines = e.target.value.split('\n').filter(line => line.trim());
              handleFieldChange('risks', lines);
            }}
            fullWidth
            multiline
            rows={6}
            size="small"
            placeholder="Enter risks, one per line..."
          />
        </CardContent>
      </Card>

      {/* Sales Prospectus Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('salesProspectus', 'sales-prospectus-section')}
          </Box>
          <TextField
            label="Sales Prospectus"
            value={formData.salesProspectus || ''}
            onChange={(e) => handleFieldChange('salesProspectus', e.target.value)}
            fullWidth
            multiline
            rows={4}
            size="small"
            placeholder="Enter sales prospectus text..."
          />
        </CardContent>
      </Card>

      {/* Legal Notes Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={4} mb={2}>
            {renderSectionHeaderControls('legalNotes', 'legal-notes-section')}
          </Box>
          <Stack spacing={2}>
            <TextField
              label="Verwahrstelle"
              value={formData.legalNotes?.verwahrstelle || ''}
              onChange={(e) => handleObjectFieldChange('legalNotes', 'verwahrstelle', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Verwaltungsgesellschaft"
              value={formData.legalNotes?.verwaltungsgesellschaft || ''}
              onChange={(e) => handleObjectFieldChange('legalNotes', 'verwaltungsgesellschaft', e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Legal Notes Text"
              value={formData.legalNotes?.text || ''}
              onChange={(e) => handleObjectFieldChange('legalNotes', 'text', e.target.value)}
              fullWidth
              multiline
              rows={12}
              size="small"
              placeholder="Enter legal notes text..."
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Top 10 Stocks Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {renderSectionHeaderControls('top10Stocks', 'top10-stocks-section')}
            <IconButton size="small" onClick={() => {
              const stocks = [...(formData.top10Stocks || []), { name: '', percentage: 0 }];
              handleFieldChange('top10Stocks', stocks);
            }} aria-label="Add stock">
              <Add fontSize="small" />
            </IconButton>
          </Box>
          <Grid container columns={12} columnSpacing={16} rowSpacing={8}>
            {(formData.top10Stocks || []).map((stock: any, index: number) => (
              <Grid item xs={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label={`Stock ${index + 1}`}
                    value={stock.name || ''}
                    onChange={(e) => {
                      const stocks = [...(formData.top10Stocks || [])];
                      stocks[index] = { ...stock, name: e.target.value };
                      handleFieldChange('top10Stocks', stocks);
                    }}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="%"
                    type="text"
                    inputProps={{ inputMode: 'decimal' }}
                    value={stock.percentage ?? ''}
                    onChange={(e) => {
                      const stocks = [...(formData.top10Stocks || [])];
                      stocks[index] = { ...stock, percentage: e.target.value };
                      handleFieldChange('top10Stocks', stocks);
                    }}
                    size="small"
                    sx={{ width: 100 }}
                  />
                  <IconButton
                    onClick={() => {
                      const stocks = [...(formData.top10Stocks || [])];
                      stocks.splice(index, 1);
                      handleFieldChange('top10Stocks', stocks);
                    }}
                    color="error"
                    size="small"
                    aria-label={`Remove stock ${index + 1}`}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Sector Allocation Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {renderSectionHeaderControls('sectorAllocation', 'sector-allocation-section')}
            <IconButton size="small" onClick={() => {
              const allocation = [...(formData.sectorAllocation || []), { label: '', value: 0 }];
              handleFieldChange('sectorAllocation', allocation);
            }} aria-label="Add sector">
              <Add fontSize="small" />
            </IconButton>
          </Box>
          <Grid container columns={12} columnSpacing={16} rowSpacing={8}>
            {(formData.sectorAllocation || []).map((item: any, index: number) => (
              <Grid item xs={12} key={`sa-${index}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label={`Label ${index + 1}`}
                    value={item.label || ''}
                    onChange={(e) => {
                      const allocation = [...(formData.sectorAllocation || [])];
                      allocation[index] = { ...item, label: e.target.value };
                      handleFieldChange('sectorAllocation', allocation);
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Value %"
                    type="number"
                    value={item.value ?? 0}
                    onChange={(e) => {
                      const allocation = [...(formData.sectorAllocation || [])];
                      allocation[index] = { ...item, value: Number(e.target.value) };
                      handleFieldChange('sectorAllocation', allocation);
                    }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                  <IconButton
                    onClick={() => {
                      const allocation = [...(formData.sectorAllocation || [])];
                      allocation.splice(index, 1);
                      handleFieldChange('sectorAllocation', allocation);
                    }}
                    color="error"
                    size="small"
                    aria-label={`Remove sector ${index + 1}`}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Currency Allocation Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {renderSectionHeaderControls('currencyAllocation', 'currency-allocation-section')}
            <IconButton size="small" onClick={() => {
              const allocation = [...(formData.currencyAllocation || []), { label: '', value: 0 }];
              handleFieldChange('currencyAllocation', allocation);
            }} aria-label="Add currency">
              <Add fontSize="small" />
            </IconButton>
          </Box>
          <Grid container columns={12} columnSpacing={16} rowSpacing={8}>
            {(formData.currencyAllocation || []).map((item: any, index: number) => (
              <Grid item xs={12} key={`ca-${index}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label={`Label ${index + 1}`}
                    value={item.label || ''}
                    onChange={(e) => {
                      const allocation = [...(formData.currencyAllocation || [])];
                      allocation[index] = { ...item, label: e.target.value };
                      handleFieldChange('currencyAllocation', allocation);
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Value %"
                    type="number"
                    value={item.value ?? 0}
                    onChange={(e) => {
                      const allocation = [...(formData.currencyAllocation || [])];
                      allocation[index] = { ...item, value: Number(e.target.value) };
                      handleFieldChange('currencyAllocation', allocation);
                    }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                  <IconButton
                    onClick={() => {
                      const allocation = [...(formData.currencyAllocation || [])];
                      allocation.splice(index, 1);
                      handleFieldChange('currencyAllocation', allocation);
                    }}
                    color="error"
                    size="small"
                    aria-label={`Remove currency ${index + 1}`}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Asset Allocation Section */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            {renderSectionHeaderControls('assetAllocation', 'asset-allocation-section')}
            <IconButton size="small" onClick={() => {
              const allocation = [...(formData.assetAllocation || []), { label: '', value: 0 }];
              handleFieldChange('assetAllocation', allocation);
            }} aria-label="Add asset">
              <Add fontSize="small" />
            </IconButton>
          </Box>
          <Grid container columns={12} columnSpacing={16} rowSpacing={8}>
            {(formData.assetAllocation || []).map((item: any, index: number) => (
              <Grid item xs={12} key={`aa-${index}`}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label={`Label ${index + 1}`}
                    value={item.label || ''}
                    onChange={(e) => {
                      const allocation = [...(formData.assetAllocation || [])];
                      allocation[index] = { ...item, label: e.target.value };
                      handleFieldChange('assetAllocation', allocation);
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Value %"
                    type="number"
                    value={item.value ?? 0}
                    onChange={(e) => {
                      const allocation = [...(formData.assetAllocation || [])];
                      allocation[index] = { ...item, value: Number(e.target.value) };
                      handleFieldChange('assetAllocation', allocation);
                    }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                  <IconButton
                    onClick={() => {
                      const allocation = [...(formData.assetAllocation || [])];
                      allocation.splice(index, 1);
                      handleFieldChange('assetAllocation', allocation);
                    }}
                    color="error"
                    size="small"
                    aria-label={`Remove asset ${index + 1}`}
                  >
                    <Remove fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
